import React from 'react';
import Plot from '../../../node_modules/react-plotly.js/react-plotly';
import { Divider, Row, Col, Space, Typography } from 'antd';
import usePlotlyAnnotations from "./usePlotlyAnnotations";
import {toRGB} from "../../utils/colorUtils";
import Button from "@mui/material/Button";
import {COOL_COLOR_PALETTE} from "../../utils/colorUtils";
// Helper function to calculate percentage change
const calculatePercentageChange = (data, normalize) => {
  if (!normalize) return data;  // Return raw data if not normalizing
  const baseValue = data[0][1]; // First value of the time series
  return data.map(([date, value]) => [date, ((value - baseValue) / baseValue)]);
};


  // Generalized X and Y axis configurations
const getXAxisConfig = (disableScrollbar, title='') => ({
  rangeslider: { visible: !disableScrollbar },  // Toggle range slider based on disableScrollbar prop
  showspikes: true,  // Show spikes on the x-axis
  spikethickness: 1,
  spikedash: 'solid',
  spikemode: 'across',  // Spikes across the whole chart
  spikedistance: -1,  // Show spikes at any point
    title
});


const getYAxisConfig = (minY, maxY, valueFormat, title='') => ({
  title,
  tickformat: valueFormat,  // Dollar format or percentage based on valueFormat
  showspikes: true,  // Show spikes on the y-axis
  spikedthickness: 1,
  spikedash: 'solid',
  spikemode: 'across',  // Spikes across the whole chart
  spikedistance: -1,  // Show spikes at any point
  range: (minY !== undefined && maxY !== undefined) ? [minY, maxY] : undefined,  // Set min/max range if provided
});

/**
 * PlotlyStockChart
 *
 * @param {Object[]} timeseriesData - Array of time series data objects. Each object represents a series.
 * @param {string} timeseriesData[].name - Name of the time series (e.g., "Portfolio").
 * @param {Array[]} timeseriesData[].data - Array of data points. Each data point is an array of the form [timestamp, value].
 * @param {string} timeseriesData[].color - Optional. Color of the series line.
 * @param {boolean} timeseriesData[].area - Optional. If true, the line will be displayed as an area chart (filled underneath).
 * @param {string} timeseriesData[].fill - Optional. Defines how the area under the line should be filled (e.g., "tozeroy").
 *
 * @param {function} onDateSelected - Callback for when a date is selected (annotation added).
 * @param {function} onDateRemoved - Callback for when a date annotation is removed.
 * @param {function} callbackOnClearAnnotations - Callback for when all annotations are cleared.
 * @param {JSX.Element} extra - Additional JSX elements (e.g., extra buttons).
 * @param {string} description - Optional. Chart description or title.
 * @param {string} xTitle - Optional. X-axis title.
 * @param {string} yTitle - Optional. Y-axis title.
 * @param {boolean} disableAnnotations - Optional. Disable annotation interactions (adding/removing).
 * @param {number} fontSize - Optional. Font size for the chart labels.
 * @param {boolean} disableScrollbar - Optional. Disable the X-axis range slider.
 * @param {Array} shadedRegion - Optional. Specifies regions to shade under the chart.
 * @param {Array<string>} verticalLines - Optional. Dates where vertical lines should be drawn on the chart.
 * @param {number} minY - Optional. Minimum Y-axis value.
 * @param {number} maxY - Optional. Maximum Y-axis value.
 * @param {boolean} normalize - Optional. Normalize the time series data to show percentage change. Defaults to true.
 * @param {string} valueFormat - Optional. Format for the Y-axis values (e.g., ".2%" for percentage, "$" for dollar values).
 *
 * @example
 * const timeseriesData = [
 *   {
 *     name: "Portfolio",
 *     data: [
 *       ["2023-01-01", 150000],  // Timestamp and value
 *       ["2023-01-02", 151000],
 *       // More data points...
 *     ],
 *     color: "#0083ff",  // Color of the line
 *     area: true,  // Display as area chart
 *     fill: 'tozeroy',  // Fill the area below the line
 *   },
 *   {
 *     name: "Benchmark",
 *     data: [
 *       ["2023-01-01", 4000],
 *       ["2023-01-02", 4050],
 *       // More data points...
 *     ],
 *     color: "#4d5d70",  // Color of the benchmark line
 *     area: false,  // Not an area chart, just a line
 *   },
 * ];
 *
 * @returns {JSX.Element}
 */
const PlotlyStockChart = ({
  timeseriesData,
  onDateSelected,
  onDateRemoved,
  callbackOnClearAnnotations,
    extra,
  description = '',  // Change title to description
  xTitle = '',  // Optional X-axis title
    yTitle = '',  // Optional Y-axis title
  disableAnnotations = false,
  disableToolbar = false,  // Prop to control toolbar
  disableScrollbar = false,  // Prop to control scrollbar
  shadedRegion = null,  // Optional shaded region
  verticalLines = [],  // List of dates to place vertical lines
  minY,  // Optional minY for Y-axis
  maxY,  // Optional maxY for Y-axis
  normalize = true,  // Normalize data by default
  valueFormat = '.2%'  // Optional value format (e.g., '$' for dollar formatting)
}) => {
  // Use the custom hook to manage annotations and shapes
 const { annotations, shapes, handleAnnotationClick,
         removeAllAnnotations } = usePlotlyAnnotations({
    onDateSelected,
    onDateRemoved,
    initialVerticalLines: verticalLines,  // Pass vertical lines to the hook
    shadedRegion

  });
  // Prepare the data for Plotly in percentage mode, handling 'color' and 'area'
  const plotData = timeseriesData.map((series, index) => {
    const percentageData = calculatePercentageChange(series.data, normalize);
    const defaultColor = COOL_COLOR_PALETTE[index % COOL_COLOR_PALETTE.length];
    return {
      x: percentageData.map(row => row[0]),  // Extracting the timestamps
      y: percentageData.map(row => row[1]),  // Extracting the percentage change
      type: "scatter",
      mode: "lines",  // Still a line plot but changes 'fill' if 'area' is true
      fill: series.fill || null,  // If 'area' is true, fill the area below the line\
      fillColor: series.area && series.color ? `${toRGB(series.color, 0.3)}` : undefined,  // Use the 'color' field if present
      line: {
        color: series.color || defaultColor,  // Use the 'color' field if present
      },
      name: series.name,
    };
  });

  // Function to handle selected range
  const handleRelayout = (eventData) => {
    if (eventData["xaxis.range[0]"] && eventData["xaxis.range[1]"]) {
      const startDate = eventData["xaxis.range[0]"];
      const endDate = eventData["xaxis.range[1]"];
      // Use these dates for any custom logic
    }
  };

  // Function to handle click events and add/remove vertical line and annotation
  const handleClick = (event) => {
    if (disableAnnotations) return;  // Disable annotations if the prop is true
    const xValue = event.points[0].x; // Extract the x (date) value from the clicked point
    handleAnnotationClick(xValue); // Use the hook's click handler
  };

  const handleRemoveAllAnnotations = () => {
    removeAllAnnotations();
    callbackOnClearAnnotations?.();
  }

  return (
    <div style={{ width: '100%', height: '100%' }}>
        {!disableToolbar && (
            <Space direction={'vertical'}>
                {description && (
                    <Typography.Text style={{fontSize: '14px'}}>{description}</Typography.Text>
                )}
                {!disableAnnotations && (
                    <Space>
                        <Button variant={'outlined'} color={'error'} onClick={handleRemoveAllAnnotations}>
                            Clear Annotations
                        </Button>
                        {extra}
                    </Space>
                )}
            </Space>
        )}
      <Plot
        data={plotData}
        layout={{
          annotations: disableAnnotations ? [] : annotations,  // Disable annotations if the prop is true
          shapes: disableAnnotations ? [] : shapes,  // Disable shapes if annotations are disabled
          hovermode: "x unified",  // Show unified crosshairs on hover
          yaxis: getYAxisConfig(minY, maxY, valueFormat, yTitle),  // Use the generalized Y axis config with min/max options
          xaxis: getXAxisConfig(disableScrollbar),  // Conditionally disable scrollbar
          dragmode: "zoom",
          responsive: true,  // Make the chart responsive
            margin: { l: 50, r: 10, t: 10, b: 50 },  // Remove padding for the title
          legend: {
            orientation: 'h',  // Set legend to horizontal
            y: 1.1,  // Position legend at the top of the chart
            x: 0.5,  // Center the legend
            xanchor: 'center',  // Anchor the legend horizontally at the center
            yanchor: 'bottom',  // Anchor the legend vertically at the bottom

          },
            font: {
              family: 'var(--font-main)', // Use your custom font from CSS variables
              color: '#000', // Optional, specify font color if needed
            },
        }}

        useResizeHandler={true}  // Make the chart responsive to container size
        style={{ width: "100%", height: "100%" }}  // Make the chart fill the container
        onRelayout={handleRelayout}  // Capture range selection events
        onClick={handleClick}  // Capture click events
        config={{ displayModeBar: false }}  // Hide the toolbar
      />
    </div>
  );
};

export default PlotlyStockChart;
